import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";

const chapters = [
  {
    title: "Institutional Authority",
    subtitle: "Authority design",
  },
  {
    title: "The Certification Gap",
    subtitle: "Operator certification",
  },
  {
    title: "Enforce or Erode",
    subtitle: "Enforcement mechanics",
  },
  {
    title: "Governance Beyond Founders",
    subtitle: "Post-founder governance",
  },
];

const BookPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="book-page">
      <SEOHead 
        title="The Authority Operating Manual - OnPoint Authority Systems"
        description="The institutional methodology behind businesses that scale without founders, license without dilution, and transfer without disruption."
        url="https://onpointauthority.com/book"
      />
      <Navbar />

      {/* Above the Fold - Hero */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12" data-testid="book-hero">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              The Authority<br />
              <span className="text-primary">Operating Manual</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground mb-8">
              The institutional methodology behind businesses that scale without founders, 
              license without dilution, and transfer without disruption.
            </p>
            <Link 
              to="/authority-review" 
              className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors group"
              data-testid="request-access-link"
            >
              Request access to the system
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What This Book Is */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="what-book-is">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              What This Book Is
            </p>
            <div className="space-y-6 text-lg font-light leading-relaxed text-muted-foreground">
              <p>
                This book is not thought leadership.<br />
                It is not motivation, strategy, or opinion.
              </p>
              <p className="text-foreground">
                It documents the operating logic behind OnPoint Authority Systems—a governed, 
                enforceable authority framework designed to replace founder dependency with 
                systems, certification, and licensing.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What This Book Replaces */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="what-book-replaces">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              What This Book Replaces
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {["Founder discretion", "Informal leadership", "Unenforced standards"].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <p className="font-serif text-2xl md:text-3xl font-light text-foreground">
                  {item}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* System Depth - Chapters */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="system-depth">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              System Depth
            </p>
            <p className="text-lg text-muted-foreground">
              Each section represents a non-negotiable layer of the authority stack.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {chapters.map((chapter, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/10 p-6"
              >
                <h3 className="font-serif text-xl font-light text-foreground mb-2">
                  {chapter.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {chapter.subtitle}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="who-for">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              Who This Is For
            </p>
            <ul className="space-y-4">
              {[
                "Operators building transferable businesses",
                "Investors evaluating founder risk",
                "Boards requiring governance continuity",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-lg text-foreground"
                >
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-3" />
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* What This Leads To */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="leads-to">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              What This Leads To
            </p>
            <p className="text-xl font-light text-foreground mb-8">
              This book is the entry point to certification, licensing, and governed authority.
            </p>
            <Link 
              to="/certification" 
              className="inline-flex items-center text-sm text-primary hover:text-primary/80 transition-colors group"
              data-testid="view-authority-path-link"
            >
              View the Authority Path
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Authority Path Flow */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="authority-flow">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12 text-center"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              The Authority Path
            </p>
          </motion.div>

          {/* Horizontal Flow Diagram */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
            {[
              { stage: "Book", desc: "Intellectual foundation", subdesc: "Public authority artifact", href: "/book" },
              { stage: "Course", desc: "Structured methodology", subdesc: "Guided system comprehension", href: "/course" },
              { stage: "Certification", desc: "Competency validation", subdesc: "Enforcement readiness", href: "/certification" },
              { stage: "License", desc: "Contractual authority", subdesc: "Audit + revocation rights", href: "/license" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="flex items-center"
              >
                <Link to={item.href} className="bg-card/10 border border-white/10 p-6 text-center min-w-[180px] hover:border-primary/30 transition-colors group">
                  <p className="font-serif text-xl text-primary mb-2 group-hover:text-primary/80">{item.stage}</p>
                  <p className="text-sm text-foreground mb-1">{item.desc}</p>
                  <p className="text-xs text-muted-foreground">{item.subdesc}</p>
                </Link>
                {index < 3 && (
                  <>
                    <div className="hidden md:flex items-center justify-center w-8">
                      <ArrowRight className="h-5 w-5 text-primary/50" />
                    </div>
                    <div className="md:hidden flex items-center justify-center h-8">
                      <ArrowRight className="h-5 w-5 text-primary/50 rotate-90" />
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="book-closing">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-serif text-2xl md:text-3xl font-light text-muted-foreground leading-relaxed">
              This book is the beginning of the authority system—
              <span className="text-foreground">not the product itself.</span>
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookPage;
